import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { CartActions } from '@spartacus/cart/base/core';
import { ActiveCartFacade, Cart } from '@spartacus/cart/base/root';
import { EventService, createFrom } from "@spartacus/core";
import { EMPTY, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { TagManagerFeatureService } from '../tag-manager-feature.service';
import { EcommerceType } from './CartEvents.model';
import {
  CustomCartAddEntrySuccessEvent,
  CustomCartRemoveEntrySuccessEvent,
  CustomCartUpdateEntrySuccessEvent,
  CustomViewCartSuccessEvent
} from './custom-cart.events';
import { NavigationEvent } from "@spartacus/storefront";
import { CustomCleanEcommerceEvent } from "../common/custom-common.event";

/**
 * Registers events for the active cart
 */
@Injectable({providedIn: 'root'})
export class CustomCartEventBuilder {
  actionType: string;

  constructor(
    protected actionsSubject: ActionsSubject,
    protected event: EventService,
    protected activeCartService: ActiveCartFacade,
    protected tagManagerFeatureService: TagManagerFeatureService,
  ) {
    this.register();
  }

  /**
   * Registers events for the active cart
   */
  protected register(): void {
    this.event.register(
      CustomCartUpdateEntrySuccessEvent,
      this.registerCustomUpdateEntry(),
    );

    this.event.register(
      CustomCartAddEntrySuccessEvent,
      this.registerCustomAddEntry(),
    );
    this.event.register(
      CustomCartRemoveEntrySuccessEvent,
      this.registerCustomRemoveEntry(),
    );

    this.event.register(
      CustomViewCartSuccessEvent,
      this.registerCustomViewCartEntry()
    )
  }

  /**
   * Register events for adding entry to the active cart
   */
  protected registerCustomAddEntry(): Observable<CustomCartAddEntrySuccessEvent> {
    return this.actionsSubject.pipe(
      ofType(CartActions.CART_ADD_ENTRY_SUCCESS),
      map((action: any) => action.payload),
      tap(() => this.event.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      switchMap(({entry, quantityAdded}) => {
        return this.activeCartService.getActive().pipe(
          take(1),
          map(activeCart => {
            return createFrom(CustomCartAddEntrySuccessEvent, {
              event: EcommerceType.ADD_TO_CART,
              ecommerce: {
                currency: entry.totalPrice.currencyIso,
                value: this.tagManagerFeatureService.getCartModifiedEventValue(entry.product, quantityAdded),
                items: [
                  this.tagManagerFeatureService.getCartModifiedProductItem(activeCart, entry, quantityAdded),
                ]
              },
            });
          }),
        );
      }),
    );
  }

  /**
   * Register events for removing entry to the active cart
   */
  protected registerCustomRemoveEntry(): Observable<CustomCartRemoveEntrySuccessEvent> {
    return this.actionsSubject.pipe(
      ofType(CartActions.CART_REMOVE_ENTRY),
      switchMap((action: CartActions.CartRemoveEntry) => {
          return of(action).pipe(
            withLatestFrom(
              this.activeCartService.getActive(),
              this.activeCartService.getActiveCartId(),
            ),
          );
        },
      ),
      // tslint:disable-next-line:variable-name
      filter(([action, _activeCart, activeCartId]) => action.payload.cartId === activeCartId),
      tap(() => this.event.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map(([action, activeCart, activeCartId]) => {
        const removedEntryNumber = parseInt(action.payload.entryNumber, 10);
        const removedEntry = activeCart.entries.find(entry => entry.entryNumber === removedEntryNumber);
        return createFrom(CustomCartRemoveEntrySuccessEvent, {
          event: EcommerceType.REMOVE_FROM_CART,
          ecommerce: {
            currency: removedEntry.totalPrice.currencyIso,
            value: this.tagManagerFeatureService.getCartModifiedEventValue(removedEntry.product, removedEntry.quantity),
            items: [
              this.tagManagerFeatureService.getCartModifiedProductItem(activeCart, removedEntry, removedEntry.quantity)
            ]
          },
        });
      }),
    );
  }

  /**
   * Register events for removing entry to the active cart
   */
  protected registerCustomUpdateEntry(): Observable<CustomCartUpdateEntrySuccessEvent | never> {
    return this.actionsSubject.pipe(
      ofType(CartActions.CART_UPDATE_ENTRY_SUCCESS),
      switchMap((action: CartActions.CartUpdateEntrySuccess) => {
        return of(action).pipe(
          withLatestFrom(
            this.activeCartService.getActive(),
            this.activeCartService.getActiveCartId(),
          ),
        );
      }),
      filter(([action, _activeCart, activeCartId]) => action.payload.cartId === activeCartId),
      switchMap(([action, activeCart, activeCartId]) => {
        const modifiedActionData = action.payload;
        const modifiedEntryNumber = parseInt(modifiedActionData.entryNumber, 10);
        const modifiedEntry = activeCart.entries.find(entry => entry.entryNumber === modifiedEntryNumber);
        const modifiedQuantity = modifiedActionData.quantity - modifiedEntry.quantity;

        // To filtered Infinite CART_UPDATE_ENTRY_SUCCESS event which is not related to the cart quantity updates
        if (modifiedQuantity === 0) {
          return EMPTY;
        }
        const eventType = modifiedQuantity > 0 ? EcommerceType.ADD_TO_CART : EcommerceType.REMOVE_FROM_CART;
        return of(createFrom(CustomCartUpdateEntrySuccessEvent, {
            event: eventType,
            ecommerce: {
              currency: modifiedEntry.totalPrice.currencyIso,
              value: this.tagManagerFeatureService.getCartModifiedEventValue(modifiedEntry.product, Math.abs(modifiedQuantity)),
              items: [
                this.tagManagerFeatureService.getCartModifiedProductItem(activeCart, modifiedEntry, Math.abs(modifiedQuantity))
              ]
            },
          })
        );
      }),
    );
  }

  /**
   * Register events for enter cart page
   */
  protected registerCustomViewCartEntry(): Observable<CustomViewCartSuccessEvent> {
    return this.event.get(NavigationEvent).pipe(
      filter(navigationEvent => navigationEvent.semanticRoute === 'cart'),
      take(1),
      switchMap((nav) => {
        return this.activeCartService.getActive().pipe(
          filter(cart => Object.keys(cart).length > 0),
          take(1),
          tap(() => this.event.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
          map((activeCart: Cart) => {
            return createFrom(CustomViewCartSuccessEvent, {
              event: EcommerceType.VIEW_CART,
              ecommerce: {
                currency: activeCart.subTotal.currencyIso,
                value: activeCart.subTotal.value,
                coupon: '',
                items: activeCart.entries.map(
                  entry => this.tagManagerFeatureService.getCartProductItem(activeCart, entry, entry.quantity))
              },
            });
          }),
        );
      }),
    )
  }
}
